import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { blogPosts as staticPosts } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState(staticPosts);
  const [expanded, setExpanded] = useState(false);
  const [cat, setCat] = useState("Tutte");

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) setPosts(data);
      })
      .catch(() => {});
  }, []);

  const categories = ["Tutte", ...new Set(posts.map((p) => p.category))];
  const sorted = [...posts].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  const filtered = cat === "Tutte" ? sorted : sorted.filter((p) => p.category === cat);
  const visible = expanded ? filtered : filtered.slice(0, 6);

  return (
    <section id="blog" data-testid="blog-section" className="bg-paper py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="eyebrow text-brandblue mb-6"
        >
          Blog & guide
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="font-serif font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl leading-[1.12] mb-10"
        >
          Consigli per affittare casa, <span className="italic text-brandblue">per proprietari e inquilini</span>
        </motion.h2>

        <div className="flex flex-wrap gap-3 mb-12" data-testid="blog-category-filters">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => {
                setCat(c);
                setExpanded(false);
              }}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 border ${
                cat === c
                  ? "bg-ink text-white border-ink"
                  : "bg-white text-ink/60 border-ink/15 hover:border-azure hover:text-brandblue"
              }`}
            >
              {c}
              <span className={`ml-2 text-xs ${cat === c ? "text-azure" : "text-ink/35"}`}>
                {c === "Tutte" ? posts.length : posts.filter((p) => p.category === c).length}
              </span>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {visible.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              data-testid={`blog-article-card-${p.slug}`}
              className="group rounded-2xl overflow-hidden bg-white border border-ink/10 hover:border-azure/40 hover:shadow-[0_16px_40px_rgba(0,157,223,0.14)] transition-[border-color,box-shadow] duration-300 flex flex-col"
            >
              <Link to={`/blog/${p.slug}`} className="block overflow-hidden">
                <img
                  src={p.img}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </Link>
                <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-brandblue">{p.category}</span>
                  {p.date && (
                    <span className="text-[11px] text-ink/40" data-testid={`blog-card-date-${p.slug}`}>
                      {new Date(p.date).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                  )}
                </div>
                <Link to={`/blog/${p.slug}`} className="mt-2">
                  <h3 className="font-serif font-semibold text-xl leading-snug group-hover:text-brandblue transition-colors duration-300">
                    {p.title}
                  </h3>
                </Link>
                <p className="mt-3 text-sm text-ink/60 leading-relaxed flex-1">{p.excerpt}</p>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-read-more-btn-${p.slug}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brandblue hover:text-azure transition-colors"
                >
                  Leggi la notizia
                  <ArrowUpRight size={15} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        {!expanded && filtered.length > 6 && (
          <div className="mt-12 text-center">
            <button
              data-testid="blog-show-all-btn"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-2 border border-ink/20 rounded-full px-7 py-3.5 text-sm font-semibold hover:bg-ink hover:text-white transition-colors duration-300"
            >
              Mostra tutti gli articoli ({filtered.length})
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
